import React, { useEffect, useState } from 'react';
import { MapPin, Users, TrendingUp, AlertCircle } from 'lucide-react';
import { CompetitorMap } from './CompetitorMap';
import { CompetitorComparisonTable } from './CompetitorComparisonTable';
import {
  fetchCompetitorsInRadius,
  fetchStationByObjectId,
  fetchStationByAddress,
  CompetitorWithDistance
} from '../../services/competitorQueryService';
import { AddressRecord } from '../../lib/supabaseClient';

interface ValuationCompetitorAnalysisProps {
  // Subject property data from valuation
  subjectData: {
    location: string;
    stationObjectId?: number;
    propertySize?: number;
    canopySize?: number;
    lotSize?: number;
    brandAffiliation?: string;
    brandName?: string;
    yearBuilt?: number;
    numberOfPumps?: number;
    hasCarWash?: boolean;
    carWashType?: string;
  };
  radiusMiles?: number;
}

export function ValuationCompetitorAnalysis({
  subjectData,
  radiusMiles = 1
}: ValuationCompetitorAnalysisProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subjectStation, setSubjectStation] = useState<AddressRecord | null>(null);
  const [competitors, setCompetitors] = useState<CompetitorWithDistance[]>([]);

  useEffect(() => {
    async function loadCompetitors() {
      try {
        setLoading(true);
        setError(null);

        // Fetch subject property details from Supabase
        // Try by OBJECTID first, then fall back to address lookup
        let station: AddressRecord | null = null;

        if (subjectData.stationObjectId) {
          station = await fetchStationByObjectId(subjectData.stationObjectId);
        }

        // Fallback: If no stationObjectId or lookup failed, try address lookup
        if (!station && subjectData.location) {
          console.log('Attempting address lookup for:', subjectData.location);
          station = await fetchStationByAddress(subjectData.location);
        }

        if (!station) {
          setError('Property not found in database');
          setLoading(false);
          return;
        }

        if (!station.Latitude || !station.Longitude) {
          setError('Subject property does not have geographic coordinates');
          setLoading(false);
          return;
        }

        setSubjectStation(station);

        // Fetch competitors within radius
        const competitorResults = await fetchCompetitorsInRadius({
          latitude: station.Latitude,
          longitude: station.Longitude,
          radiusMiles,
          excludeObjectId: station.OBJECTID
        });

        setCompetitors(competitorResults);
        setLoading(false);
      } catch (err) {
        console.error('Error loading competitors:', err);
        setError('Failed to load competitor data');
        setLoading(false);
      }
    }

    loadCompetitors();
  }, [subjectData.stationObjectId, subjectData.location, radiusMiles]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="flex flex-col items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading competitor analysis...</p>
        </div>
      </div>
    );
  }

  if (error || !subjectStation) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Competitor Analysis Unavailable
            </h3>
            <p className="text-gray-600 mb-4">
              {error || 'Unable to load competitor data for this property'}
            </p>
            <p className="text-sm text-gray-500">
              This feature requires the property to be in our database (currently Riverside and San Bernardino Counties) with geographic coordinates.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Competitor Analysis</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Market intelligence within {radiusMiles} mile radius
                </p>
              </div>
            </div>
          </div>

          <div className="text-right">
            <div className="text-3xl font-bold text-blue-600">{competitors.length}</div>
            <div className="text-sm text-gray-600">Competitors Found</div>
          </div>
        </div>
      </div>

      {/* Interactive Map */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            Your property and nearby competitors within 1 mile
          </p>
        </div>

        <CompetitorMap
          subjectProperty={{
            latitude: subjectStation.Latitude!,
            longitude: subjectStation.Longitude!,
            address: subjectStation.Facility_Address
          }}
          competitors={competitors}
          radiusMiles={radiusMiles}
        />
      </div>

      {/* Comparison Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            Property Comparison
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Detailed comparison of physical attributes and location
          </p>
        </div>

        <CompetitorComparisonTable
          subjectProperty={{
            address: subjectData.location,
            buildingSize: subjectData.propertySize,
            canopySize: subjectData.canopySize,
            lotSize: subjectData.lotSize,
            brand: subjectData.brandAffiliation === 'branded' ? subjectData.brandName : 'Unbranded',
            yearBuilt: subjectData.yearBuilt,
            numberOfPumps: subjectData.numberOfPumps,
            serviceBays: subjectStation.Service_Bay_Count,
            ustCount: subjectStation.UST_Count
          }}
          competitors={competitors}
          radiusMiles={radiusMiles}
        />
      </div>
    </div>
  );
}
